import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { VerifiedBadge } from '../../components/Badges';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { TextArea } from '../../components/Field';
import { Icon } from '../../components/Icon';
import { Menu, type MenuItem } from '../../components/Menu';
import { ErrorNotice, Loading } from '../../components/States';
import { useToast } from '../../components/Toast';
import { createReply, deleteReply, editReply, getReplies, type Reply } from '../../lib/api';
import { linkify } from '../../lib/linkify';
import { fullTime, relativeTime } from '../../lib/time';
import { useMe } from '../auth/session';
import { useRefreshBoards } from '../feed/queries';
import { BlockDialog } from './BlockDialog';
import { ReportDialog } from './ReportDialog';
import styles from './Replies.module.css';
import safety from './Safety.module.css';

const MAX = 1000;

interface Props {
  postId: string;
  /** Replies are closed on deleted/removed notes. */
  canReply: boolean;
}

export function Replies({ postId, canReply }: Props) {
  const replies = useQuery({ queryKey: ['replies', postId], queryFn: () => getReplies(postId) });
  const { data: me } = useMe();
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const suspended = me?.status === 'suspended';

  if (replies.isPending) return <Loading label="Loading replies" />;
  if (replies.isError) return <ErrorNotice error={replies.error} onRetry={() => replies.refetch()} />;

  const all = replies.data;
  const childrenOf = (id: string) => all.filter((r) => r.parentId === id && !r.hiddenReason);
  // A hidden reply only leaves a placeholder when visible replies hang from it.
  const topLevel = all.filter((r) => !r.parentId && (!r.hiddenReason || childrenOf(r.id).length > 0));
  const allowReply = canReply && !suspended;

  return (
    <section className={styles.section} aria-labelledby="replies-title">
      <h2 id="replies-title" className={styles.title}>
        Replies
      </h2>
      {topLevel.length === 0 && <p className={styles.empty}>No replies yet.</p>}
      <ol className={styles.list}>
        {topLevel.map((reply) => (
          <li key={reply.id}>
            <ReplyItem reply={reply} />
            <div className={styles.replyActions}>
              {allowReply && !reply.hiddenReason && replyingTo !== reply.id && (
                <button type="button" className={styles.replyButton} onClick={() => setReplyingTo(reply.id)}>
                  <Icon name="reply" size={16} />
                  Reply to {reply.author.name}
                </button>
              )}
            </div>
            {(childrenOf(reply.id).length > 0 || replyingTo === reply.id) && (
              <ol className={styles.children}>
                {childrenOf(reply.id).map((child) => (
                  <li key={child.id}>
                    <ReplyItem reply={child} />
                  </li>
                ))}
                {replyingTo === reply.id && (
                  <li>
                    <ReplyComposer
                      postId={postId}
                      parentId={reply.id}
                      label={`Reply to ${reply.author.name}`}
                      onDone={() => setReplyingTo(null)}
                    />
                  </li>
                )}
              </ol>
            )}
          </li>
        ))}
      </ol>
      {allowReply && <ReplyComposer postId={postId} parentId={null} label="Write a reply" />}
      {suspended && <p className={styles.empty}>Your account is suspended, so you can't reply right now.</p>}
    </section>
  );
}

function ReplyItem({ reply }: { reply: Reply }) {
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState<'report' | 'block' | 'delete' | null>(null);
  const toast = useToast();
  const refresh = useRefreshBoards();
  const name = reply.author.name ?? 'this neighbor';

  const remove = useMutation({
    mutationFn: () => deleteReply(reply.id),
    onSuccess: async () => {
      setDialog(null);
      toast.show('Reply deleted');
      await refresh();
    },
  });

  if (reply.hiddenReason) {
    const text = {
      deleted: 'This reply was deleted.',
      removed: 'A moderator removed this reply.',
      blocked: "This reply is from someone you've blocked or who blocked you.",
    }[reply.hiddenReason];
    return <p className={styles.placeholder}>{text}</p>;
  }

  const items: MenuItem[] = reply.isOwn
    ? [
        { label: 'Edit reply', icon: 'pencil', onSelect: () => setEditing(true) },
        { label: 'Delete reply', icon: 'close', danger: true, onSelect: () => setDialog('delete') },
      ]
    : [
        { label: 'Report reply', icon: 'flag', onSelect: () => setDialog('report') },
        { label: `Block ${name}`, icon: 'block', danger: true, onSelect: () => setDialog('block') },
      ];

  return (
    <article className={styles.reply} aria-labelledby={`reply-${reply.id}`}>
      <Avatar name={reply.author.name} hue={reply.author.hue} size={36} />
      <div className={styles.bubble}>
        <div className={styles.head}>
          <Link id={`reply-${reply.id}`} to={`/u/${reply.author.id}`} className={styles.name}>
            {name}
          </Link>
          {reply.author.isNeighbor && <VerifiedBadge />}
          <time className={styles.when} dateTime={reply.createdAt} title={fullTime(reply.createdAt)}>
            {relativeTime(reply.createdAt)}
            {reply.editedAt && ' (edited)'}
          </time>
          <span className={styles.menu}>
            <Menu label="Reply actions" items={items} />
          </span>
        </div>
        {editing ? (
          <EditReply reply={reply} onDone={() => setEditing(false)} />
        ) : (
          <p className={styles.text}>{linkify(reply.body ?? '')}</p>
        )}
      </div>

      {reply.isOwn ? (
        <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)} title="Delete this reply?">
          <p>It will be replaced with a short “deleted” note.</p>
          {remove.error && <ErrorNotice error={remove.error} />}
          <div className={safety.actions}>
            <Button variant="quiet" onClick={() => setDialog(null)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              Delete reply
            </Button>
          </div>
        </Dialog>
      ) : (
        <>
          <ReportDialog
            open={dialog === 'report'}
            onClose={() => setDialog(null)}
            target={{ replyId: reply.id }}
            subject="this reply"
          />
          <BlockDialog open={dialog === 'block'} onClose={() => setDialog(null)} userId={reply.author.id} name={name} />
        </>
      )}
    </article>
  );
}

function EditReply({ reply, onDone }: { reply: Reply; onDone: () => void }) {
  const [body, setBody] = useState(reply.body ?? '');
  const refresh = useRefreshBoards();
  const save = useMutation({
    mutationFn: () => editReply(reply.id, body),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form className={styles.composer} onSubmit={submit}>
      <TextArea
        label="Edit reply"
        hideLabel
        value={body}
        maxLength={MAX}
        rows={3}
        onChange={(e) => setBody(e.target.value)}
        autoFocus
      />
      {save.error && <ErrorNotice error={save.error} />}
      <div className={styles.composerButtons}>
        <Button variant="quiet" size="small" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="small" disabled={!body.trim() || save.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}

function ReplyComposer({
  postId,
  parentId,
  label,
  onDone,
}: {
  postId: string;
  parentId: string | null;
  label: string;
  onDone?: () => void;
}) {
  const [body, setBody] = useState('');
  const refresh = useRefreshBoards();
  const send = useMutation({
    mutationFn: () => createReply(postId, body, parentId),
    onSuccess: async () => {
      setBody('');
      await refresh();
      onDone?.();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (body.trim()) send.mutate();
  };

  return (
    <form className={styles.composer} onSubmit={submit}>
      <TextArea
        label={label}
        hideLabel
        placeholder={`${label}…`}
        value={body}
        maxLength={MAX}
        rows={parentId ? 2 : 3}
        autoFocus={Boolean(parentId)}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
        }}
      />
      {send.error && <ErrorNotice error={send.error} />}
      <div className={styles.composerButtons}>
        {onDone && (
          <Button variant="quiet" size="small" onClick={onDone}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="small"
          disabled={!body.trim() || body.length > MAX || send.isPending}
        >
          {send.isPending ? 'Replying…' : 'Reply'}
        </Button>
      </div>
    </form>
  );
}
